import React from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { UserX, UserCheck } from 'lucide-react';
import type { UserWithDetails } from '../../hooks/useUsers';
import { isAuditRole } from '../../utils/roles';

interface UserStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  user: UserWithDetails;
}

export function UserStatusModal({ isOpen, onClose, onConfirm, user }: UserStatusModalProps) {
  const isDeactivating = user.status === 'active';
  const isAuditor = isAuditRole(user.role);

  const title = isAuditor
    ? (isDeactivating ? "Retirer l'expert" : "Réactiver l'expert")
    : `${isDeactivating ? 'Désactivation' : 'Activation'} de l'utilisateur`;

  const actionLabel = isAuditor
    ? (isDeactivating ? 'Retirer de l\'organisation' : 'Réactiver')
    : (isDeactivating ? 'Désactiver' : 'Activer');

  const confirmText = isAuditor
    ? (isDeactivating
        ? <>Êtes-vous sûr de vouloir retirer l'expert <strong>{user.name}</strong> de votre organisation ?</>
        : <>Êtes-vous sûr de vouloir réactiver l'expert <strong>{user.name}</strong> ?</>)
    : <>Êtes-vous sûr de vouloir {isDeactivating ? 'désactiver' : 'activer'} l'utilisateur <strong>{user.name}</strong> ?</>;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
    >
      <div className="space-y-4">
        <div className={`p-4 rounded-lg ${
          isDeactivating ? 'bg-red-50 dark:bg-red-900/20' : 'bg-green-50 dark:bg-green-900/20'
        }`}>
          <div className="flex items-center">
            {isDeactivating ? (
              <UserX className="h-5 w-5 text-red-600 dark:text-red-400 mr-2" />
            ) : (
              <UserCheck className="h-5 w-5 text-success mr-2" />
            )}
            <p className={`text-sm ${
              isDeactivating ? 'text-red-600 dark:text-red-400' : 'text-success'
            }`}>
              {confirmText}
            </p>
          </div>
        </div>

        {isDeactivating && (
          <div className="text-sm text-muted-foreground">
            {isAuditor ? (
              <>
                <p>Le retrait d'un expert :</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Révoque son accès aux données de votre organisation</li>
                  <li>Ne supprime pas son compte (il peut avoir d'autres organisations)</li>
                  <li>Peut être annulé à tout moment</li>
                </ul>
              </>
            ) : (
              <>
                <p>La désactivation d'un utilisateur :</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Empêche toute connexion à l'application</li>
                  <li>Conserve l'historique des actions</li>
                  <li>Peut être annulée à tout moment</li>
                </ul>
              </>
            )}
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-4">
          <Button
            variant="secondary"
            onClick={onClose}
          >
            Annuler
          </Button>
          <Button
            variant={isDeactivating ? 'warning' : 'success'}
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            {actionLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}